import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import {priority as buttonPriorities, buttonTypes, themes, CustomPropShapes} from '../../constants';
import Link from '../link/Link';

import styles from './Button.pcss';

/** The component renders a CTA button using the html button element or the Link component when we have th `to` param. */
class Button extends PureComponent {
    static displayName = 'Button';
    static propTypes = {
        shouldForceHover: PropTypes.bool,
        children: PropTypes.node.isRequired,
        className: PropTypes.string,
        disabled: PropTypes.bool,
        id: PropTypes.string,
        onClick: PropTypes.func,
        onMouseEnter: PropTypes.func,
        onMouseLeave: PropTypes.func,
        /** toggles background hover effects */
        hasHover: PropTypes.bool,
        /** gives the button a minWidth **/
        hasMinWidth: PropTypes.bool,
        /** scales down on xs-md **/
        scalesDown: PropTypes.bool,
        /** used together with the `to` property to set the correct behavior of the Link component */
        isExternalUrl: PropTypes.bool,
        /** renders the Link component when this param has a value */
        to: PropTypes.string,
        /** one of the buttonTypes from common constants */
        type: CustomPropShapes.oneOfConstant(buttonTypes),
        /** one of the priority from common constants */
        priority: CustomPropShapes.oneOfConstant(buttonPriorities),
        /** one of the themes from common constants, overwrites any custom theme from Theme.js */
        theme: CustomPropShapes.oneOfConstant(themes),
        /** hide theme color except on hover */
        shouldHideTheme: PropTypes.bool,
        /** the target attribute to be placed on the anchor */
        target: PropTypes.string
    };

    static defaultProps = {
        disabled: false,
        hasHover: true,
        hasMinWidth: true,
        scalesDown: false,
        type: buttonTypes.BUTTON,
        priority: buttonPriorities.PRIMARY
    };

    render() {
        const {
            children,
            className,
            disabled,
            hasHover,
            hasMinWidth,
            id,
            isExternalUrl,
            onClick,
            onMouseEnter,
            onMouseLeave,
            priority,
            scalesDown,
            theme,
            to,
            type,
            shouldForceHover,
            shouldHideTheme,
            target
        } = this.props;

        const classes = cx(
            styles.main,
            styles[priority],
            {
                [styles.hasHover]: hasHover && !disabled,
                [styles.hasMinWidth]: hasMinWidth,
                [styles.hideTheme]: shouldHideTheme,
                [styles.scalesDown]: scalesDown,
                [styles[theme]]: theme,
                [styles.shouldForceHover]: shouldForceHover
            },
            className
        );

        if (to && !disabled) {
            return (
                <Link
                    className={cx(styles.mainStatic, classes)}
                    id={id}
                    isExternalUrl={isExternalUrl}
                    onClick={onClick}
                    onMouseLeave={onMouseLeave}
                    onMouseEnter={onMouseEnter}
                    to={to}
                    target={target}>
                    {children}
                </Link>
            );
        }

        return (
            <button
                className={classes}
                disabled={disabled}
                id={id}
                onClick={onClick}
                onMouseLeave={onMouseLeave}
                onMouseEnter={onMouseEnter}
                type={type}>
                <span>{children}</span>
            </button>
        );
    }
}

export default Button;
